/**
 * Application-wide constants: container limits, pallet dimensions, loading method semantics.
 * Single source of truth for fill calculations and UI (StoreCart, AdminQuotationBuilder, etc.).
 * Worker keeps a copy of loading-method semantics in src/worker/shared.ts (see there).
 */

import type { ContainerType } from '@/lib/store-types';

/** Regex to detect palletized loading from loading_method name (EN "Palletized" / ES "Paletizada"). */
export const LOADING_METHOD_PALLETIZED_NAME_PATTERN = /pallet|palet/i;

/** True when the given loading method name (from DB) indicates palletized load. */
export function isLoadingMethodPalletized(loadingMethodName: string | null | undefined): boolean {
  return LOADING_METHOD_PALLETIZED_NAME_PATTERN.test((loadingMethodName ?? '').trim());
}

/** Regex to detect MOQ unit "box/caja" for mix block candidate filter. */
export const MOQ_UNIT_BOX_NAME_PATTERN = /caja|box/i;

/** True when the given MOQ unit name indicates full-container MOQ (e.g. "Contenedor 40'", "Container"). */
export function isContainerMoq(moqUnitName: string | null | undefined): boolean {
  const s = (moqUnitName ?? '').toLowerCase();
  return s.includes('container') || s.includes('contenedor');
}

/** Pallet standard (euro): fixed length and width; height is variable by product (box layers). */
export const PALLET_STANDARD_LENGTH_CM = 120;
export const PALLET_STANDARD_WIDTH_CM = 80;
/** Default height when product box dimensions are not available. */
export const PALLET_STANDARD_HEIGHT_CM = 100;
/** Max pallet height (container clearance). */
export const PALLET_MAX_HEIGHT_CM = 120;

/**
 * Limits per container type (one unit). Pallets and weight are app constants; not stored per product-origin.
 * Dry: 20ft 32 CBM, 40ft 69 CBM. Refer: 20 Ref 28 CBM, 40 Ref 58 CBM. Pallets: 20=11, 40=21, 20 Ref=9, 40 Ref=19.
 * CBM = Floor Loaded / usable volume per container.
 */
export const CONTAINER_LIMITS: Record<
  ContainerType,
  { maxWeightKg: number; maxCbm: number; maxPallets: number }
> = {
  20: { maxWeightKg: 22000, maxCbm: 32, maxPallets: 11 },
  40: { maxWeightKg: 27000, maxCbm: 69, maxPallets: 21 },
  '20REF': { maxWeightKg: 22000, maxCbm: 28, maxPallets: 9 },
  '40REF': { maxWeightKg: 27000, maxCbm: 58, maxPallets: 19 },
};

/**
 * Master list of LATAM & Americas ports for GEO pSEO (product pages show all as "Destinos Frecuentes").
 * Must stay in sync with worker DEFAULT_MASTER_PORTS when editing.
 */
export const ALL_LATAM_PORTS = [
  'Caucedo', 'Haina', 'Mariel', 'La Habana', 'Santiago de Cuba', 'Kingston',
  'San Juan', 'Freeport', 'Port of Spain', 'Port-au-Prince',
  'Zona Libre de Colón', 'Manzanillo', 'Balboa', 'Cristóbal',
  'Puerto Limón', 'Moín', 'Caldera', 'Puerto Cortés',
  'Santo Tomás de Castilla', 'Puerto Barrios', 'Puerto Quetzal', 'Acajutla', 'Corinto',
  'Puerto Cabello', 'La Guaira', 'Maracaibo', 'Cartagena', 'Barranquilla', 'Santa Marta',
  'Buenaventura',
  'Guayaquil', 'Manta', 'Callao', 'Paita',
  'San Antonio', 'Valparaíso', 'Iquique', 'Santos', 'Paranaguá', 'Suape',
  'Montevideo', 'Buenos Aires',
  'Miami', 'Port Everglades', 'Houston', 'Veracruz', 'Altamira',
  'Manzanillo (MX)', 'Lázaro Cárdenas',
];

/** ISO 3166-1 alpha-2 codes for LATAM/Caribbean countries used in OfferShippingDetails JSON-LD. */
export const LATAM_COUNTRIES = [
  'AR', 'BO', 'BR', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'SV', 'GT', 'HN',
  'MX', 'NI', 'PA', 'PY', 'PE', 'PR', 'UY', 'VE', 'BS', 'JM', 'TT', 'HT',
];

/** ISO date (YYYY-MM-DD) — update when /privacy substantive content changes. */
export const PRIVACY_POLICY_LAST_UPDATED = '2026-05-27';

/**
 * When VITE_SUPABASE_* are missing at build time, the SPA still mounts using these placeholders.
 * Business: marketing/legal pages must not white-screen; store/admin calls fail until real env is embedded.
 * Host is a valid *.supabase.co shape so @supabase/supabase-js accepts createClient().
 */
export const SUPABASE_PLACEHOLDER_URL = 'https://placeholder-not-configured.supabase.co';
/** JWT-shaped dummy (three segments); not a real project key — only satisfies client constructor. */
export const SUPABASE_PLACEHOLDER_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24ifQ.placeholder-not-configured';

/**
 * TrustBar logos: `logo.clearbit.com` often fails (DNS / product sunset). Prefer pinned CDNs + Commons.
 * Simple Icons slug list: https://github.com/simple-icons/simple-icons — version pinned for stable URLs.
 */
export const SIMPLE_ICONS_TRUST_BAR_VERSION = '16.12.0';

function trustBarSimpleIcon(slug: string): string {
  return `https://cdn.jsdelivr.net/npm/simple-icons@${SIMPLE_ICONS_TRUST_BAR_VERSION}/icons/${slug}.svg`;
}

/** Remote logo URLs for TrustBar partners (non-/public/partners). */
export const TRUST_BAR_REMOTE_LOGOS = {
  cloudflare: trustBarSimpleIcon('cloudflare'),
  digitalOcean: trustBarSimpleIcon('digitalocean'),
  vultr: trustBarSimpleIcon('vultr'),
  /** AWS not in Simple Icons under a stable slug we rely on; Commons is durable. */
  aws: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg',
  openai: 'https://upload.wikimedia.org/wikipedia/commons/4/4d/OpenAI_Logo.svg',
  comcast: 'https://upload.wikimedia.org/wikipedia/commons/8/87/Comcast.svg',
  /** Favicons via DuckDuckGo when no single stable vector CDN exists for the brand. */
  antler: 'https://icons.duckduckgo.com/ip3/antler.co.ico',
  valvenStudio: 'https://icons.duckduckgo.com/ip3/valvenstudio.com.ico',
  entain: 'https://icons.duckduckgo.com/ip3/entaingroup.com.ico',
} as const;

/** Padmora LLC — sole legal entity (Wyoming, USA). */
export const PADMORA_LEGAL_NAME = 'Padmora LLC';
export const PADMORA_EIN = '32-0708681';
export const PADMORA_FORMATION_STATE = 'Wyoming';

/** Trust badge / legal copy where EIN adds credibility. */
export const PADMORA_REGISTRATION_BADGE_LABEL = `US LLC registered (EIN ${PADMORA_EIN})`;
export const PADMORA_REGISTRATION_BADGE_TOOLTIP = `${PADMORA_LEGAL_NAME}, registered in ${PADMORA_FORMATION_STATE}, USA`;

/** Generic tagline when full LLC details are not needed. */
export const PADMORA_USA_REGISTERED = 'Registered in USA';

/** Default SEO description for Padmora (USA operations). */
export const PADMORA_DEFAULT_SEO_DESCRIPTION =
  'AI-powered boutique trading firm specializing in LC-backed commodity deals (FOB→CFR) with bank-grade compliance. USA operations.';

/** Default page title suffix for marketing pages. */
export const PADMORA_DEFAULT_SEO_TITLE =
  'Padmora — AI-powered commodity trading & trade finance | USA';

/** Primary contact for general enquiries (opens the user's email client). */
export const PADMORA_ENQUIRY_EMAIL = 'michel@padmora.com';
export const PADMORA_ENQUIRY_MAILTO = `mailto:${PADMORA_ENQUIRY_EMAIL}`;
export const PADMORA_ENQUIRY_CTA_LABEL = 'Send enquiry';
export const PADMORA_ENQUIRY_CTA_LABEL_ES = 'Enviar consulta';

/** Store card description truncation (category / family grids). */
export const STORE_CARD_DESCRIPTION_ELLIPSIS = '...';
/** Matches Tailwind `leading-5` (1.25rem) — keep in sync with StoreCardDescription. */
export const STORE_CARD_DESCRIPTION_LINE_HEIGHT_REM = 1.25;
export const STORE_CARD_DESCRIPTION_DEFAULT_LINES = 3;
